<script>
import { useUiStore } from '../store/ui.js';

export default {
    name: 'Toasts',
    computed: {
        ui() { return useUiStore(); },
    },
    mounted() {
        // Cmd-Z / Ctrl-Z → fire the most recent actionable toast.
        // Matches the Gmail-style "Undo Send" reflex operators already
        // have. The toast's natural 8 s TTL bounds the shortcut window:
        // once the toast auto-dismisses, Cmd-Z falls back to native
        // browser behaviour.
        window.addEventListener('keydown', this.onKeydown);
    },
    beforeUnmount() {
        window.removeEventListener('keydown', this.onKeydown);
    },
    methods: {
        toneClasses(type) {
            return {
                success: 'border-success/30 bg-success-soft text-success',
                error:   'border-danger/30 bg-danger-soft text-danger',
                warn:    'border-warn/30 bg-warn-soft text-warn',
                info:    'border-accent/20 bg-accent-soft text-accent',
            }[type] || 'border-line bg-surface text-ink';
        },
        icon(type) {
            return {
                success: 'M5 12l5 5L20 7',
                error:   'M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z',
                warn:    'M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z',
                info:    'M12 16v-4M12 8h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z',
            }[type] || '';
        },
        // Keyboard-driven undo. Only fires when:
        //   • Cmd-Z (Mac) or Ctrl-Z (Win/Linux) pressed
        //   • not Shift-modified (Shift-Cmd-Z is "redo" — leave to browser)
        //   • the event target isn't an editable surface (input,
        //     textarea, contenteditable) — would clobber text-edit
        //     undo, which the user definitely meant
        //   • at least one toast in the queue has an action attached
        //
        // Picks the MOST RECENT actionable toast (highest id) so a
        // sequence of cancellations gets undone in reverse order if
        // the operator hits Cmd-Z multiple times.
        onKeydown(ev) {
            if (ev.key !== 'z' && ev.key !== 'Z') return;
            if (ev.shiftKey) return;
            if (!(ev.metaKey || ev.ctrlKey)) return;
            const t = ev.target;
            const tag = t?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || t?.isContentEditable) return;

            // Find latest actionable toast. Iterating from the end is
            // O(n) but n is tiny (toasts cap at ~5 in practice).
            const list = this.ui.toasts;
            for (let i = list.length - 1; i >= 0; i--) {
                if (list[i]?.action?.onClick) {
                    ev.preventDefault();
                    this.ui.invokeToastAction(list[i].id);
                    return;
                }
            }
            // No actionable toast → fall through to native browser
            // undo (most pages don't have one, but harmless either way).
        },
    },
};
</script>

<template>
    <div class="fixed top-4 right-4 z-[100] flex flex-col gap-2 w-[360px] max-w-[calc(100vw-32px)] pointer-events-none">
        <transition-group name="toast" tag="div" class="flex flex-col gap-2">
            <div v-for="t in ui.toasts" :key="t.id"
                 :class="['pointer-events-auto rounded-lg border bg-surface shadow-pop px-3.5 py-3 flex items-start gap-2.5', toneClasses(t.type)]">
                <svg class="h-4 w-4 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path :d="icon(t.type)" />
                </svg>
                <div class="flex-1 min-w-0">
                    <div v-if="t.title" class="text-sm font-medium leading-tight">{{ t.title }}</div>
                    <div class="text-sm leading-snug" :class="t.title ? 'text-ink-muted mt-0.5' : ''">{{ t.message }}</div>
                </div>
                <!-- Action button — for "Undo"-style recovery. Inherits
                     the toast's tone via currentColor so a success-tone
                     toast gets a success-tone button. Sits to the left
                     of the dismiss-X so the operator hits the action
                     instead of accidentally dismissing it. The title
                     attribute surfaces the Cmd-Z hotkey so the keyboard
                     reflex gets reinforced. -->
                <button v-if="t.action"
                        @click="ui.invokeToastAction(t.id)"
                        v-tooltip="`Tastenkürzel: ⌘Z / Strg+Z`"
                        class="text-sm font-medium underline-offset-2 hover:underline whitespace-nowrap -my-0.5">
                    {{ t.action.label }}
                </button>
                <button @click="ui.dismissToast(t.id)"
                        class="text-ink-muted hover:text-ink ml-1 -mr-1 -mt-1 p-0.5">
                    <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
            </div>
        </transition-group>
    </div>
</template>

<style scoped>
.toast-enter-active, .toast-leave-active { transition: all 200ms ease-out; }
.toast-enter-from { opacity: 0; transform: translateX(8px); }
.toast-leave-to   { opacity: 0; transform: translateX(8px); }
.toast-leave-active { position: absolute; right: 0; }
</style>
